import he from 'he';
import type { Redirect } from 'next';
import Head from 'next/head';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getRentals, goToRentals } from '@/actions/rentals';
import PopularPostsList from '@/components/blog/PopularPostsList';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { RentalsInlineList } from '@/components/common/RentalsList/RentalsInlineList';
import SocialShare from '@/components/common/SocialShare';
import AuthorCardAndDescription from '@/components/route/blog/AuthorCardAndDescription';
import { BlogPostInlineHero } from '@/components/route/blog/BlogPostInlineHero';
import BlogStaysBanner from '@/components/route/blog/BlogStaysBanner';
import BottomNav from '@/components/route/blog/BottomNav';
import CategoryCrumbs from '@/components/route/blog/CategoryCrumbs';
import RentalRigs from '@/components/route/blog/RentalRigs';
import SuggestedPosts from '@/components/route/blog/SuggestedPosts';
import Heading from '@/components/switchback/Heading/Heading';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { useCaptainBlogAI } from '@/hooks/useCaptainBlogAI';
import { useContentTimeToRead } from '@/hooks/useContentTimeToRead';
import useScript from '@/hooks/useScript';
import { EListingSource } from '@/services/analytics/listings/types';
import { getCategoryPosts, SuccessPostResponse } from '@/services/getBlogCategoryPosts';
import getBlogPost, { IBlogPostFull } from '@/services/getBlogPost';
import { trackEvent } from '@/services/track-event';
import { IBlogPost } from '@/services/types/blog/post.types';
import withRedirect from '@/utility/withRedirect';

import css from './BlogPostSlug.module.css';

declare global {
  interface Window {
    Trailblazer?: any;
  }
}

function hasNoFollowTag(data: IBlogPostFull) {
  if (data && data.tags) {
    for (const tag of data.tags) {
      if (tag.name && tag.name.toLowerCase() === 'nofollow') {
        return true;
      }
    }
  }
  return false;
}

function isThisPageRedirected(data: IBlogPostFull) {
  if (!data?.outdoorsy_redirect?.url) return false;

  try {
    const url = new URL(data.outdoorsy_redirect.url);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function getRedirectConfig(post: IBlogPostFull): Redirect | false {
  if (hasNoFollowTag(post)) {
    return {
      permanent: true,
      destination: '/blog',
    };
  }

  if (isThisPageRedirected(post)) {
    const statusCode = parseInt(post.outdoorsy_redirect?.type ?? '301', 10) as
      | 301
      | 302
      | 307
      | 308;
    return {
      statusCode: statusCode,
      destination: post.outdoorsy_redirect?.url || '/blog',
    };
  }

  return false;
}

export const getServerSideProps = withRedirect(
  { destination: '/blog', permanent: false },
  async ({ params = {} }) => {
    const { blogPostSlug: slug } = params;
    if (!slug || typeof slug !== 'string') {
      throw new Error('No slug for blog post provided. Is the slug not a string?');
    }
    const post = await getBlogPost({ slug });

    //if post has tag 'nofollow' it is page that we don`t want to show - see ticket PLG-18
    //or if redirected by WP admin see PLG-239
    const redirect = getRedirectConfig(post);
    if (redirect) {
      return { redirect };
    }

    let posts: { posts: IBlogPost[] } = { posts: [] };
    try {
      posts = await getCategoryPosts(post?.categories?.[0]?.slug, 3, post.id);
    } catch {
      // gracefully fail if category posts not found.
    }
    const topPosts = await getCategoryPosts('top-blogs', 20);
    return { props: { post, posts: posts.posts, topPosts } };
  },
);

interface IBlogPostProps {
  post: IBlogPostFull;
  posts: IBlogPost[];
  topPosts: SuccessPostResponse;
}

const BlogPost: React.FC<IBlogPostProps> = ({
  post: {
    author,
    categories,
    content,
    date,
    images,
    next,
    previous,
    slug,
    title,
    seoDescription,
    publishedDate,
    modifiedDate,
  },
  posts,
  topPosts,
}) => {
  const intl = useIntl();
  const newsletterTitle = intl.formatMessage({
    defaultMessage: 'Be the first to get doses of destination inspiration, and discount codes.',
    id: 'RdFytN',
  });
  const pageType = 'Blog page';
  const dispatch = useDispatch();
  const getPageRentals = useCallback((filters: any) => dispatch(getRentals(filters)), [dispatch]);
  const isTrailblazerLoaded = useScript(
    '//d1o5877uy6tsnd.cloudfront.net/sdk/v4/trailblazer-widget.js',
  );

  const [didSetupTrailblazer, setDidSetupTrailblazer] = useState<boolean>(false);
  const timeToRead = useContentTimeToRead(content);

  const hideSocialPost = (currentSlug: string): boolean => {
    const slugsToHide = ['2026-soccer-guide'];
    return slugsToHide.includes(currentSlug);
  };

  useCaptainBlogAI();

  useEffect(() => {
    getPageRentals({ count: 5 });

    trackEvent({
      event: 'Blog : Viewed page',
      pageType: 'blog page',
    });
  }, [getPageRentals]);

  // Make sure the ugly JSON is hidden until we can set up TB.
  useLayoutEffect(() => {
    const tbRoot = document.getElementById('tbRoot');
    if (tbRoot) {
      tbRoot.style.display = 'none';
    }
    const tbRootClass = document.getElementsByClassName('tbRoot');
    Array.from(tbRootClass).forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });
  }, []);

  // Only try to set up TB if the script loaded, and if we didn't already set it up.
  useEffect(() => {
    if (!isTrailblazerLoaded || didSetupTrailblazer) return;
    const tbRootID = document.getElementById('tbRoot');
    const tbRootElements = Array.from(document.getElementsByClassName('tbRoot'));
    if (tbRootID) {
      tbRootElements.push(tbRootID);
    }
    if (tbRootElements.length && window.Trailblazer) {
      tbRootElements.forEach((tbRoot, i) => {
        let tbData;
        const tbDataRaw = tbRoot.innerHTML.replace(/[“”‘’″]/g, `"`);
        tbRoot.innerHTML = '';
        try {
          tbData = JSON.parse(tbDataRaw);
        } catch {
          return;
        }
        const tb = window.Trailblazer({
          ...tbData,
          mapsConfig: {
            apiKey: process.env.googleMapsApiKey,
            provider: 'google',
          },
        });
        const rootElementId = `tbRoot-${i}`;
        tbRoot.setAttribute('id', rootElementId);
        tb.initPage({ pageType: 'search', rootElementId });
        tb.start();
        (tbRoot as HTMLElement).style.display = 'block';
      });

      setDidSetupTrailblazer(true);
    }
  }, [isTrailblazerLoaded, didSetupTrailblazer]);

  return (
    <>
      <Head>
        <title>{title ? `${he.decode(String(title))} | Outdoorsy.com` : 'Outdoorsy'}</title>
        <link rel="canonical" href={`https://www.outdoorsy.com/blog/${slug}`} />

        {/* Need to get SEO description field without using Yoast */}
        <meta name="description" content={seoDescription} />

        <meta property="fb:app_id" content="604465049688341" />
        <meta name="msvalidate.01" content="1DD3784B3E7F8CD7B4C595940717F938" />
        <meta name="apple-itunes-app" content="app-id=1443835927, affiliate-data=1010lvba" />
        <meta
          name="google-site-verification"
          content="DuUf3p3Pr3-gmZfpMIg3gyG75gl7C3JpXWb0-ilQJbI"
        />

        {intl.locale === 'en-us' ? (
          <meta name="robots" content="index, follow, max-image-preview:large" />
        ) : (
          <meta name="robots" content="noindex" />
        )}

        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${title} | Outdoorsy.com`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`https://www.outdoorsy.com/blog/${slug}`} />
        <meta property="og:site_name" content="Outdoorsy RV Blogs" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={images?.full?.source_url} />
        <meta property="og:image:width" content={String(images?.full?.width)} />
        <meta property="og:image:height" content={String(images?.full?.height)} />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />

        <meta name="twitter:card" content="summary" />
      </Head>
      <BlogStaysBanner blogPost />

      <MaxWidth
        collapseMobile
        className="flex-col-reverse mx-auto mt-6 lg:grid lg:gap-16 grid-cols-1 md:grid-cols-5 lg:grid-cols-5">
        {!hideSocialPost(slug) && (
          <div className="items-start justify-start pb-16 col-span-1 sm:hidden md:flex lg:flex lg:pt-16 lg:sticky lg:top-0">
            <div className="lg:sticky lg:top-0">
              <div className="mx-4 lg:mx-0">
                <Heading level={3} className="mt-2 mb-3 text-gray-500 highlight autoType600">
                  {intl.formatMessage({ defaultMessage: 'Share', id: 'OKhRC6' })}
                </Heading>
                {!!title && (
                  <SocialShare url={`https://www.outdoorsy.com/blog/${slug}`} title={title} />
                )}
                {!!posts.length && (
                  <>
                    <hr className="mt-3" />
                    <Heading level={3} className="mt-8 mb-3 text-gray-500 highlight autoType600">
                      {intl.formatMessage({ defaultMessage: 'Suggested posts', id: 'LSRut9' })}
                    </Heading>
                  </>
                )}
              </div>
              {!!posts.length && <SuggestedPosts posts={posts} />}
            </div>
          </div>
        )}
        <div className={`mx-4 lg:mx-0 ${hideSocialPost(slug) ? 'lg:col-span-5' : 'lg:col-span-3'}`}>
          <BlogPostInlineHero
            title={title}
            author={author.name}
            date={date}
            images={images}
            timeToRead={timeToRead}
          />

          <div className="overflow-x-auto grid grid-cols-1">
            <CategoryCrumbs categories={categories} />
          </div>
          {!!content && (
            <article
              className={`prose md:prose-lg lg:prose-xl ${css.BlogPost}`}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
          <div className="my-8">
            <AuthorCardAndDescription
              name={author.name}
              avatar={author.avatar}
              description={author.description}
            />
          </div>
          <div className="my-6">
            <BottomNav
              previous={{ slug: previous.slug, title: previous.title }}
              next={{ slug: next.slug, title: next.title }}
            />
          </div>
        </div>
        {!hideSocialPost(slug) && <div className="hidden col-span-1 lg:block">&nbsp;</div>}
      </MaxWidth>
      <MaxWidth className="m-auto mt-12">
        <hr />
        <h2 className="mx-4 my-8 font-medium lg:mx-0 text-800">
          {intl.formatMessage({ defaultMessage: 'Ready to get started.', id: 'EMpVYE' })}
        </h2>
        <RentalsInlineList
          goToRentals={() => dispatch(goToRentals())}
          eventSource={EListingSource.GRID_LAYOUT}
        />
      </MaxWidth>

      <MaxWidth collapseMobile className="m-auto my-12">
        <h2 className="mx-4 my-8 font-medium lg:mx-0 text-800">
          {intl.formatMessage({ defaultMessage: 'Travel the way you want it.', id: '7ndkZ9' })}
        </h2>
        <RentalRigs />
      </MaxWidth>

      <PopularPostsList topPosts={topPosts} />

      <div className="py-2 mt-4 bg-white">
        <MaxWidth collapseMobile className="m-auto my-12">
          <NewsLetterSignup title={newsletterTitle} source={pageType} />
        </MaxWidth>
      </div>
    </>
  );
};

export default BlogPost;
